// src/components/financials/GivingUnitsManager.js
// Updated: 03/07/2026 - Fixed ESLint warnings (Removed unused imports, added useCallback)

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, 
  Table, TableBody, TableCell, TableHead, TableRow, 
  Autocomplete, Chip, IconButton, Dialog, DialogTitle, 
  DialogContent, DialogActions, Tabs, Tab, FormControlLabel,
  Checkbox, LinearProgress, Alert, Tooltip
} from '@mui/material';
import { GroupAdd, Delete, Edit, Person, PersonOutline, MergeType, Warning, Restore, VerifiedUser } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { 
  collection, getDocs, updateDoc, 
  doc, query, where, writeBatch, serverTimestamp, setDoc 
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const GivingUnitsManager = () => {
  const { userProfile } = useAuth();
  const [tab, setTab] = useState(0); 
  
  const [units, setUnits] = useState([]); 
  const [guests, setGuests] = useState([]); 
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [unitOpen, setUnitOpen] = useState(false);
  const [editingUnitId, setEditingUnitId] = useState(null);
  const [unitName, setUnitName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [migrateHistory, setMigrateHistory] = useState(false);

  const [guestOpen, setGuestOpen] = useState(false);
  const [editingGuestId, setEditingGuestId] = useState(null);
  const [guestForm, setGuestForm] = useState({ name: '', address: '', city: '', state: '', zip: '', isOfficialMember: false });

  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [guestToMerge, setGuestToMerge] = useState(null);
  const [targetMember, setTargetMember] = useState(null);

  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [identifiers, setIdentifiers] = useState({ envelopeNumber: '', cashAppTag: '', zelleID: '' });

  const churchId = userProfile?.churchId;

  // Wrapped in useCallback to resolve ESLint warning
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const unitsSnap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
      const allUnits = unitsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Separate normal units from Guests/Offline Profiles
      setUnits(allUnits.filter(u => u.type === 'unit'));
      setGuests(allUnits.filter(u => u.type === 'guest' || u.isGuest === true || u.isOfflineMember === true));

      const usersQ = query(collection(db, 'users'), where('churchId', '==', churchId));
      const usersSnap = await getDocs(usersQ);
      setAllUsers(usersSnap.docs.map(d => ({ 
        id: d.id, 
        name: `${d.data().firstName} ${d.data().lastName}`,
        email: d.data().email,
        ...d.data()
      })));
    } catch (e) {
      console.error("Fetch Error:", e);
    }
    setLoading(false);
  }, [churchId]);

  useEffect(() => {
    if (churchId) fetchData();
  }, [churchId, fetchData]);

  const handleSaveUnit = async () => {
    if (!unitName || selectedMembers.length === 0) return alert("Name and members required.");
    setLoading(true);
    
    try {
      const batch = writeBatch(db);
      let unitRef;
      const memberIds = selectedMembers.map(u => u.id);
      
      if (editingUnitId) {
        unitRef = doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', editingUnitId);
        
        const existingUnit = units.find(u => u.id === editingUnitId);
        if (existingUnit && existingUnit.memberIds) {
            const removedIds = existingUnit.memberIds.filter(oldId => !memberIds.includes(oldId));
            
            removedIds.forEach(remId => {
                const userRef = doc(db, 'users', remId);
                batch.update(userRef, { isIndependentGiver: true });
            });
        }
      } else {
        unitRef = doc(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
      }

      batch.set(unitRef, {
        name: unitName,
        isGuest: false,
        type: 'unit',
        memberIds: memberIds,
        memberNames: selectedMembers.map(u => u.name),
        updatedAt: serverTimestamp()
      }, { merge: true });

      selectedMembers.forEach(member => {
        const userRef = doc(db, 'users', member.id);
        batch.update(userRef, { isIndependentGiver: false });
      });

      if (migrateHistory) {
        const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
        const txSnap = await getDocs(query(txRef, where('donorId', 'in', memberIds)));
        
        txSnap.docs.forEach(tDoc => {
          batch.update(tDoc.ref, { 
            donorId: unitRef.id,
            originalDonorId: tDoc.data().donorId 
          });
        });
      }

      await batch.commit();
      setUnitOpen(false);
      setMigrateHistory(false);
      fetchData();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
    setLoading(false);
  };

  const handleUnmergeMember = async (unit, memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from this household? Transactions will be restored to their individual profile.`)) return;
    
    setLoading(true);
    try {
      const batch = writeBatch(db);
      const newMemberIds = unit.memberIds.filter(id => id !== memberId);
      const newMemberNames = allUsers
        .filter(u => newMemberIds.includes(u.id))
        .map(u => u.name);
        
      const unitRef = doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', unit.id);
      
      if (newMemberIds.length === 0) {
        batch.delete(unitRef);
      } else {
        batch.update(unitRef, {
          memberIds: newMemberIds,
          memberNames: newMemberNames
        });
      }

      const userRef = doc(db, 'users', memberId);
      batch.update(userRef, { isIndependentGiver: true });

      const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
      const q = query(txRef, where('donorId', '==', unit.id), where('originalDonorId', '==', memberId));
      const txSnap = await getDocs(q);
      
      txSnap.docs.forEach(tDoc => {
        batch.update(tDoc.ref, { 
          donorId: memberId,
          originalDonorId: null 
        });
      });

      await batch.commit();
      fetchData();
    } catch (e) {
      alert("Unmerge failed: " + e.message);
    }
    setLoading(false);
  };

  const handleSaveGuest = async () => {
    if (!guestForm.name) return;
    try {
      const payload = { 
          ...guestForm, 
          isGuest: !guestForm.isOfficialMember, 
          isOfflineMember: guestForm.isOfficialMember,
          type: 'guest', 
          memberIds: [], 
          memberNames: [guestForm.name] 
      };
      
      const ref = editingGuestId 
        ? doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', editingGuestId)
        : doc(collection(db, 'churches', churchId, 'financials', 'data', 'givingUnits'));
      
      await setDoc(ref, payload, { merge: true });
      setGuestOpen(false);
      fetchData();
    } catch (e) {
      alert("Save failed: " + e.message);
    }
  };

  const handleDeleteUnit = async (unit) => {
    if(!window.confirm(`Delete ${unit.name}? This will restore ALL transactions to their original individual owners.`)) return;
    setLoading(true);
    try {
        const batch = writeBatch(db);

        if (unit.memberIds && unit.memberIds.length > 0) {
            unit.memberIds.forEach(mid => {
                const userRef = doc(db, 'users', mid);
                batch.update(userRef, { isIndependentGiver: true });
            });
        }

        const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
        const q = query(txRef, where('donorId', '==', unit.id));
        const txSnap = await getDocs(q);

        txSnap.docs.forEach(tDoc => {
            const data = tDoc.data();
            if (data.originalDonorId) {
                batch.update(tDoc.ref, {
                    donorId: data.originalDonorId,
                    originalDonorId: null
                });
            }
        });

        const unitRef = doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', unit.id);
        batch.delete(unitRef);

        await batch.commit();
        fetchData();
    } catch (e) {
        alert("Delete failed: " + e.message);
    }
    setLoading(false);
  };

  const handleSaveUserIdentifiers = async () => {
    await updateDoc(doc(db, 'users', editingUser.id), { financialIdentifiers: identifiers });
    setEditUserOpen(false);
    fetchData();
  };

  const handleMergeGuest = async () => {
    if (!guestToMerge || !targetMember) return alert("Select a member to merge into.");
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      const guestRef = doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', guestToMerge.id);
      batch.update(guestRef, { 
          merged: true, 
          mergedToId: targetMember.id,
          mergedToName: targetMember.name,
          updatedAt: serverTimestamp() 
      });

      const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
      const q = query(txRef, where('donorId', '==', guestToMerge.id));
      const txSnap = await getDocs(q);
      
      txSnap.docs.forEach(tDoc => {
        batch.update(tDoc.ref, { 
          donorId: targetMember.id,
          originalGuestId: guestToMerge.id 
        });
      });

      await batch.commit();
      setMergeDialogOpen(false);
      setGuestToMerge(null);
      setTargetMember(null);
      fetchData();
    } catch (e) {
      alert("Merge failed: " + e.message);
    }
    setLoading(false);
  };

  const handleUndoGuestMerge = async (guest) => {
    if (!window.confirm(`Undo merge for ${guest.name}? Their history will be pulled back from ${guest.mergedToName}.`)) return;
    setLoading(true);
    try {
      const batch = writeBatch(db);
      
      const guestRef = doc(db, 'churches', churchId, 'financials', 'data', 'givingUnits', guest.id);
      batch.update(guestRef, { 
          merged: false, 
          mergedToId: null,
          mergedToName: null,
          updatedAt: serverTimestamp() 
      });

      const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
      const q = query(txRef, where('donorId', '==', guest.mergedToId), where('originalGuestId', '==', guest.id));
      const txSnap = await getDocs(q);
      
      txSnap.docs.forEach(tDoc => {
        batch.update(tDoc.ref, { 
          donorId: guest.id,
          originalGuestId: null 
        });
      });

      await batch.commit();
      fetchData();
    } catch (e) {
      alert("Undo failed: " + e.message);
    }
    setLoading(false);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} centered>
          <Tab icon={<Person />} label="Members" />
          <Tab icon={<GroupAdd />} label="Households" />
          <Tab icon={<PersonOutline />} label="Offline Profiles" />
        </Tabs>
      </Paper>
      
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {tab === 0 && (
        <Paper>
          <Table size="small">
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Status</TableCell><TableCell>Envelope #</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
            <TableBody>
              {allUsers.sort((a,b) => a.name.localeCompare(b.name)).map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.name} <Typography variant="caption" display="block">{user.email}</Typography></TableCell>
                  <TableCell>
                    <Chip 
                      label={user.isIndependentGiver !== false ? "Independent" : "Linked to Unit"} 
                      size="small" 
                      color={user.isIndependentGiver !== false ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>{user.financialIdentifiers?.envelopeNumber || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => {
                       setEditingUser(user);
                       setIdentifiers({
                         envelopeNumber: user.financialIdentifiers?.envelopeNumber || '',
                         cashAppTag: user.financialIdentifiers?.cashAppTag || ''
                       });
                       setEditUserOpen(true);
                    }}><Edit fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Button variant="contained" startIcon={<MergeType />} onClick={() => { setEditingUnitId(null); setUnitName(''); setSelectedMembers([]); setUnitOpen(true); }}>
              New Household Merge
            </Button>
          </Box>
          <Paper>
            <Table>
              <TableHead><TableRow><TableCell>Household Name</TableCell><TableCell>Members (Click 'x' to Unmerge)</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {units.map((unit) => (
                  <TableRow key={unit.id} hover>
                    <TableCell sx={{ fontWeight: 'bold' }}>{unit.name}</TableCell>
                    <TableCell>
                      {unit.memberNames?.map((name, idx) => (
                        <Chip 
                          key={idx} 
                          label={name} 
                          size="small" 
                          onDelete={() => handleUnmergeMember(unit, unit.memberIds[idx], name)} 
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => { 
                        setEditingUnitId(unit.id); 
                        setUnitName(unit.name); 
                        setSelectedMembers(allUsers.filter(u => unit.memberIds.includes(u.id))); 
                        setUnitOpen(true); 
                      }}><Edit /></IconButton>
                      <IconButton onClick={() => handleDeleteUnit(unit)} color="error"><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ textAlign: 'right', mb: 2 }}>
            <Button variant="contained" onClick={() => { setEditingGuestId(null); setGuestForm({ name: '', address: '', city: '', state: '', zip: '', isOfficialMember: false }); setGuestOpen(true); }}>Add Offline Donor</Button>
          </Box>
          <Paper>
            <Table>
              <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Status</TableCell><TableCell>Tax Address</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {guests.map((g) => (
                  <TableRow key={g.id} sx={{ opacity: g.merged ? 0.6 : 1 }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                        {g.name}
                        {g.merged && <Chip size="small" label={`Merged to ${g.mergedToName}`} color="warning" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell>
                        {!g.merged && (
                            g.isOfflineMember ? (
                                <Chip label="Offline Member" color="success" size="small" icon={<VerifiedUser />} variant="outlined" />
                            ) : (
                                <Chip label="Guest / Visitor" color="info" size="small" variant="outlined" />
                            )
                        )}
                    </TableCell>
                    <TableCell>
                        {!g.merged && (g.address ? `${g.address}, ${g.city}` : <Typography color="error" variant="caption">Missing Address</Typography>)}
                    </TableCell>
                    <TableCell align="right">
                      {g.merged ? (
                          <Tooltip title="Undo Merge and Restore Transactions">
                              <IconButton onClick={() => handleUndoGuestMerge(g)} color="warning"><Restore /></IconButton>
                          </Tooltip>
                      ) : (
                          <>
                              <Tooltip title="Merge Profile into App User">
                                  <IconButton onClick={() => { setGuestToMerge(g); setTargetMember(null); setMergeDialogOpen(true); }} color="secondary"><MergeType /></IconButton>
                              </Tooltip>
                              <IconButton onClick={() => { setEditingGuestId(g.id); setGuestForm({ ...g }); setGuestOpen(true); }}><Edit /></IconButton>
                              <IconButton onClick={() => handleDeleteUnit(g)} color="error"><Delete /></IconButton>
                          </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      <Dialog open={unitOpen} onClose={() => setUnitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUnitId ? "Edit Household" : "Household Merge"}</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Household Name (for Statements)" value={unitName} onChange={(e) => setUnitName(e.target.value)} sx={{ mb: 3, mt: 1 }} />
          <Autocomplete multiple options={allUsers} getOptionLabel={(o) => o.name} value={selectedMembers} onChange={(e, val) => setSelectedMembers(val)} renderInput={(p) => <TextField {...p} label="Select Members to Merge" />} />
          {!editingUnitId && (
            <Box sx={{ mt: 3, p: 2, bgcolor: '#fffde7', borderRadius: 1 }}>
                <FormControlLabel
                  control={<Checkbox checked={migrateHistory} onChange={(e) => setMigrateHistory(e.target.checked)} />}
                  label="Migrate individual giving history to this household"
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                    <Warning fontSize="inherit" /> Warning: This will update past transactions for selected members.
                </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setUnitOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSaveUnit} disabled={loading}>Save & Merge</Button></DialogActions>
      </Dialog>

      <Dialog open={guestOpen} onClose={() => setGuestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGuestId ? "Edit Offline Profile" : "New Offline Profile"}</DialogTitle>
        <DialogContent dividers>
            <TextField fullWidth label="Name" value={guestForm.name} onChange={(e)=>setGuestForm({...guestForm, name: e.target.value})} sx={{ mt: 1, mb: 2 }} />
            
            <Box sx={{ mb: 2, p: 1, bgcolor: '#f1f8e9', borderRadius: 1 }}>
                <FormControlLabel
                    control={<Checkbox checked={guestForm.isOfficialMember} onChange={(e) => setGuestForm({...guestForm, isOfficialMember: e.target.checked})} color="success" />}
                    label={<Typography variant="body2" fontWeight="bold">Mark as Official Church Member</Typography>}
                />
                <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4 }}>
                    Check this if they are a committed member who simply doesn't use the app. Uncheck if they are a guest/visitor.
                </Typography>
            </Box>

            <TextField fullWidth label="Street Address" value={guestForm.address} onChange={(e)=>setGuestForm({...guestForm, address: e.target.value})} sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField fullWidth label="City" value={guestForm.city} onChange={(e)=>setGuestForm({...guestForm, city: e.target.value})} />
                <TextField label="State" sx={{ width: 80 }} value={guestForm.state} onChange={(e)=>setGuestForm({...guestForm, state: e.target.value})} />
                <TextField label="Zip" sx={{ width: 100 }} value={guestForm.zip} onChange={(e)=>setGuestForm({...guestForm, zip: e.target.value})} />
            </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setGuestOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSaveGuest}>Save</Button></DialogActions>
      </Dialog>

      <Dialog open={mergeDialogOpen} onClose={() => setMergeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge Profile into App User</DialogTitle>
        <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 3 }}>
                You are about to merge <strong>{guestToMerge?.name}</strong> into a verified member account. All giving history will be reassigned to the selected app user.
            </Alert>
            <Autocomplete 
                options={allUsers} 
                getOptionLabel={(o) => o.name} 
                value={targetMember} 
                onChange={(e, val) => setTargetMember(val)} 
                renderInput={(p) => <TextField {...p} label="Search for Target Member" autoFocus />} 
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleMergeGuest} disabled={loading || !targetMember} color="secondary">
                Confirm Merge
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editUserOpen} onClose={() => setEditUserOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Individual Identifiers</DialogTitle>
        <DialogContent dividers>
            <TextField fullWidth margin="dense" label="Envelope Number" value={identifiers.envelopeNumber} onChange={(e) => setIdentifiers({...identifiers, envelopeNumber: e.target.value})} />
            <TextField fullWidth margin="dense" label="CashApp Tag" value={identifiers.cashAppTag} onChange={(e) => setIdentifiers({...identifiers, cashAppTag: e.target.value})} />
        </DialogContent>
        <DialogActions><Button onClick={() => setEditUserOpen(false)}>Cancel</Button><Button variant="contained" onClick={handleSaveUserIdentifiers}>Save</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default GivingUnitsManager;